import { QueryClient } from '@tanstack/react-query';

import { supabase } from '~/utils/supabaseClient';

export type Device = {
  device_id: string;
  device_name: string;
  device_type: string;
  device_metadata: Record<string, any>;
  created_at: string;
  device_auth_id: string;
  device_config: {
    job_pausing: boolean;
    enforce_station_picking: boolean;
    dev_mode_enabled?: boolean;
    work_order_scanning_type?: 'qr_code_and_pin' | 'work_order_level';
    annotation_viewer?: boolean;
    fast_flow?: boolean;
    default_to_work_order_name_lookup?: boolean;
  };
};

export const deviceDetailsQuery = (id: string) => ({
  queryKey: ['device', 'details', id],
  queryFn: async () => {
    const { data, error } = await supabase
      .from('devices')
      .select()
      .eq('device_id', id)
      // devices.device_id is UNIQUE, so a failure here could only ever mean zero rows - an
      // unregistered device, or one scoped out by RLS. That is the expected 404 below, but
      // `.single()` raised it as a PostgREST 406/PGRST116, which
      // @supabase/sentry-js-integration reports to Sentry. See ARTIC-HUB-NC.
      .maybeSingle<Device>();

    if (error) {
      // Absence is no longer an error, so this is a real failure - surfacing it keeps
      // outages out of the 404 page where they were previously indistinguishable.
      throw new Error(error.message);
    }

    if (!data) {
      throw new Response('', {
        status: 404,
        statusText: 'Not Found',
      });
    }

    if (data.device_metadata.current_job_card?.job_card_id) {
      const jobCard = await supabase
        .from('job_cards')
        .select('job_card_operation_name')
        .eq('job_card_id', data.device_metadata.current_job_card.job_card_id)
        // The referenced job card may already be gone (job cards are deleted on rebind),
        // which the `?.` below already tolerates - so absence must not raise a PostgREST
        // error the Sentry integration would report.
        .maybeSingle();
      data!.device_metadata!.current_job_card.job_card_operation_name =
        jobCard.data?.job_card_operation_name;
    } else if (data.device_metadata?.current_job_card_ids) {
      const jobCards = await supabase
        .from('job_cards')
        .select('job_card_operation_name, job_card_id')
        .in('job_card_id', data.device_metadata?.current_job_card_ids);
      data!.device_metadata!.current_job_cards = jobCards.data;
    }
    return data as Device;
  },
});

export const clearDeviceStation = async (deviceId: string) => {
  const { data } = await supabase
    .from('devices')
    .select('device_metadata')
    .eq('device_id', deviceId)
    .single<{
      device_metadata: Record<string, unknown>;
    }>();
  const { error } = await supabase
    .from('devices')
    .update({
      device_metadata: {
        ...data?.device_metadata,
        current_station: null,
      },
    })
    .eq('device_id', deviceId);
  if (error) {
    throw error;
  }
  return;
};

export const clearDeviceJobCardsAndBatch = async (deviceId: string) => {
  const { data } = await supabase
    .from('devices')
    .select('device_metadata')
    .eq('device_id', deviceId)
    .single<{
      device_metadata: Record<string, unknown>;
    }>();
  const { error } = await supabase
    .from('devices')
    .update({
      device_metadata: {
        ...data?.device_metadata,
        current_job_card: null,
        current_job_card_ids: null,
        current_batch_process_id: null,
      },
    })
    .eq('device_id', deviceId);

  if (error) {
    throw error;
  }
  return;
};

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: any) => {
    const query = deviceDetailsQuery(params.deviceId);
    return queryClient.getQueryData(query as any) ?? (await queryClient.fetchQuery(query));
  };
